<script setup lang="ts">
import { rpcHttpKey } from "@uicomps/common/helpers";
import { computed, ref } from "vue";
import CodeInput from "../../CodeInput.vue";
import {
  EmailCodeType,
  isCodeValid,
  signupData,
  signupDataDirty,
} from "../data";
import useStepEmits from "./useStepEmits";
import isMobile from "@shared/IsMobile";
import { useI18n } from "vue-i18n";
defineEmits(["stepComplete", "stepLoading"]);

const { emitStepCompleted, emitStepLoading } = useStepEmits();
const { t, locale } = useI18n();
const checkedEmails = {};
const isEmailAvailable = ref(false);
const hasError = ref(false);
let checkEmailAvailabilityTimeout = null;

const isSendEmailTimeout = ref(false);
let sendCodeAgainTimeout = null;
const isCodeSent = ref(false);
const codeLabel = ref(t("pages.signup.email.send-code-label"));
const isCodeResponseError = ref(null);
const isCodeError = ref(false);

const ageConsentGiven = ref(false);
const termsOfServiceAgreed = ref(false);
const isEmailValid = computed(
  () =>
    signupDataDirty.email &&
    /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(signupDataDirty.email),
);
async function requestEmailCode() {
  if (
    isSendEmailTimeout.value ||
    !isEmailValid.value ||
    !isEmailAvailable.value
  ) {
    return;
  }
  isSendEmailTimeout.value = true;
  const sendEmailCodeResponse = await rpcHttpKey(
    "UserRequestEmailVerificationCode",
    {
      email: signupData.email,
      locale: locale.value,
    },
  );
  isCodeSent.value = true;
  clearTimeout(sendCodeAgainTimeout);
  sendCodeAgainTimeout = setTimeout(() => {
    isSendEmailTimeout.value = false;
  }, 60_000);
  if (sendEmailCodeResponse.message) {
    codeLabel.value = sendEmailCodeResponse.message;
    isCodeResponseError.value = false;
  } else if (sendEmailCodeResponse.error) {
    codeLabel.value = sendEmailCodeResponse.error;
    isCodeResponseError.value = true;
  }
}
const codeLabelClass = computed(() => {
  if (!isCodeSent.value) {
    return "invisible";
  }
  if (
    signupData.emailVerificationCode.length === 6 &&
    isCodeError.value === false
  ) {
    return "color-text--green-150";
  } else if (isCodeResponseError.value || isCodeError.value) {
    return "color-text--bright-red-100";
  }
  return "";
});
async function handleCodeInput(code: string) {
  codeLabel.value = t("pages.signup.email.send-code-label");
  signupData.emailVerificationCode = code;
  isCodeError.value = null;
  if (signupData.emailVerificationCode.length !== 6) {
    return;
  }
  isCodeError.value = !(await isCodeValid(
    signupData.email,
    signupData.emailVerificationCode,
    EmailCodeType.EmailVerification,
  ));
  if (isCodeError.value) {
    codeLabel.value = t("pages.signup.code.incorrect");
    hasError.value = true;
  } else {
    codeLabel.value = t("pages.signup.code.correct");
    hasError.value = false;
  }
  handleStepProgress();
}
function handleInput() {
  emitStepCompleted(false);
  hasError.value = false;
  clearTimeout(checkEmailAvailabilityTimeout);
  if (!isEmailValid.value) {
    return;
  }
  isEmailAvailable.value = checkedEmails[signupDataDirty.email];
  if (isEmailAvailable.value !== undefined) {
    handleStepProgress();
    hasError.value = !isEmailAvailable.value;
  } else {
    checkEmailAvailabilityTimeout = setTimeout(async () => {
      await checkIsEmailAvailable();
    }, 300);
  }
  handleStepProgress();
}

function handleStepProgress() {
  if (
    isEmailValid.value &&
    isEmailAvailable.value &&
    isCodeSent.value &&
    !isCodeError.value &&
    ageConsentGiven.value &&
    termsOfServiceAgreed.value &&
    signupData.emailVerificationCode.length == 6
  ) {
    emitStepCompleted(true);
  } else {
    emitStepCompleted(false);
  }
}

async function checkIsEmailAvailable() {
  emitStepLoading(true);
  isEmailAvailable.value = await rpcHttpKey("UserIsEmailAvailable", {
    email: signupDataDirty.email,
  });
  if (isEmailAvailable.value) {
    signupData.email = signupDataDirty.email;
  } else {
    signupData.email = "";
  }
  emitStepLoading(false);

  checkedEmails[signupDataDirty.email] = isEmailAvailable.value;
  hasError.value = !isEmailAvailable.value;
  handleStepProgress();
}
</script>
<template>
  <label v-depixel for="email">{{ t("global.your-email") }}</label>
  <div class="input-wrapper flex align--center pos--rel">
    <text-input
      type="email"
      name="email"
      v-model="signupDataDirty.email"
      :errorText="
        hasError && !isEmailAvailable
          ? t('pages.signup.email.error.already-taken')
          : ''
      "
      @input="handleInput"
    />
    <span
      v-depixel
      class="font--xssm pos--abs offset-r--s"
      :class="
        isSendEmailTimeout || !isEmailAvailable || !isEmailValid
          ? 'color-text--blue-100'
          : 'color-text--blue-400 pointer'
      "
      @click="requestEmailCode"
    >
      {{
        isSendEmailTimeout
          ? t("pages.signup.email.send-code-success")
          : t("pages.signup.email.send-code")
      }}
    </span>
  </div>
  <checkbox v-model="ageConsentGiven" @input="handleInput">
    {{ t("pages.signup.email.consent-age") }}
  </checkbox>
  <checkbox v-model="termsOfServiceAgreed" @input="handleInput">
    {{ t("pages.signup.email.consent-legal") }}
    <a href="https://bithotel.io/#/terms" target="_blank">{{
      t("pages.signup.email.tos")
    }}</a>
    &
    <a href="https://bithotel.io/#/eula" target="_blank">{{
      t("pages.signup.email.eula")
    }}</a>
  </checkbox>
  <label
    v-depixel-normal
    for="email-verification-code"
    class="text-center"
    :class="codeLabelClass"
  >
    {{ codeLabel }}
  </label>

  <code-input
    ref="verificationCodeInput"
    :field-height="isMobile() ? 38 : 48"
    :field-width="isMobile() ? 38 : 48"
    :fields="6"
    :required="true"
    class="code-input font depixel normal"
    type="number"
    :disabled="!isCodeSent"
    @change="handleCodeInput"
    @paste="handleCodeInput"
  />
</template>
<style scoped lang="scss">
.invisible {
  opacity: 0;
}
label.text-center {
  font-size: 16px !important;
}
.input-wrapper:deep() {
  span {
    transition: all 300ms ease;
    padding: get-size(xxxs) get-size(xxsm);
    border-radius: get-size(xs);
    &.pointer {
      background-color: rgba(get-color(green-75), 0.5);
      text-shadow: 0 2px 6px get-color(green-100);
      outline: 1px solid get-color(green-100);
      box-shadow: 0 4px 6px 4px #ddd;
    }
  }
  input {
    padding-right: get-size(xxl);
  }
}

// KEEP IT SCOPED BRUV
// use :deep() to apply styles to the child component
/*
    .this-component:deep() { <- :deep() must be in parent node
        .child-component{ <- this has <style scoped> also defined

        }
    }
*/
</style>
